import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useApiClient } from '@/lib/api';

interface ReadinessContextType {
    isReady: boolean;
    missingItems: string[];
    isLoading: boolean;
    checkReadiness: () => Promise<void>;
}

const ReadinessContext = createContext<ReadinessContextType | null>(null);

export const useReadiness = () => {
    const context = useContext(ReadinessContext);
    if (!context) {
        throw new Error('useReadiness must be used within a ReadinessProvider');
    }
    return context;
};

export const ReadinessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isReady, setIsReady] = useState<boolean>(false);
    const [missingItems, setMissingItems] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { token, user } = useAuth();
    const { api } = useApiClient();

    const checkReadiness = async (forceRefresh = false) => {
        if (!token || !user) {
            setIsReady(false);
            setMissingItems([]);
            setIsLoading(false);
            return { is_ready: false, missing_items: [] };
        }

        try {
            setIsLoading(true);

            const response = await api.checkReadiness();

            setIsReady(response.is_ready);
            setMissingItems(response.missing_items);

            return response;
        } catch (error) {
            console.error('Error checking readiness:', error);
            setIsReady(false);
            setMissingItems([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        checkReadiness();
    }, [token, user]);

    const value = {
        isReady,
        missingItems,
        isLoading,
        checkReadiness
    };

    return <ReadinessContext.Provider value={value}>{children}</ReadinessContext.Provider>;
}; 